import clsx from "clsx";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";

interface MultiSelectDropdownProps<T extends { id: string }> {
  items: T[];
  selectedIds: string[];
  onSelectedIdsChange: (selectedIds: string[]) => void;
  placeholder?: string;
  renderItem: (item: T, isSelected: boolean) => React.ReactNode;
  renderSelectedItem?: (item: T, itemCount: number) => React.ReactNode;
  disabled?: boolean;
  emptyMessage?: string;
  className?: string;
}

function MultiSelectDropdown<T extends { id: string }>({
  items,
  selectedIds,
  onSelectedIdsChange,
  placeholder = "Selecione...",
  renderItem,
  renderSelectedItem,
  disabled = false,
  emptyMessage = "Nenhum item disponível",
  className = "",
}: MultiSelectDropdownProps<T>) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const selectedItems = items.filter((item) => selectedIds.includes(item.id));

  const handleSelectItem = (itemId: string) => {
    onSelectedIdsChange(
      selectedIds.includes(itemId) ? selectedIds.filter((id) => id !== itemId) : [...selectedIds, itemId]
    );
  };

  const getDisplayContent = () => {
    if (selectedItems.length === 0) {
      return <span className="text-gray-500">{placeholder}</span>;
    }

    if (renderSelectedItem && selectedItems.length > 0) {
      return renderSelectedItem(selectedItems[0], selectedItems.length);
    }

    return (
      <div className="flex flex-col">
        <span className="font-medium text-left">
          {selectedItems.length === 1
            ? String((selectedItems[0] as any).name || (selectedItems[0] as any).id)
            : `${(selectedItems[0] as any).name || (selectedItems[0] as any).id} +${selectedItems.length - 1}`}
        </span>
        <span className="text-xs text-gray-500">
          {selectedItems.length === 1 ? "item selecionado" : `${selectedItems.length} items selecionados`}
        </span>
      </div>
    );
  };

  return (
    <div className={className}>
      {items.length === 0 ? (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center text-gray-500">
          <p>{emptyMessage}</p>
        </div>
      ) : (
        <div className="relative">
          <button
            type="button"
            disabled={disabled}
            className={clsx(
              "w-full flex items-center justify-start px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent",
              disabled && "bg-gray-100 text-gray-400 cursor-not-allowed hover:bg-gray-100"
            )}
            onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
          >
            <ChevronDown
              className={clsx("text-gray-400 transition-transform", isDropdownOpen ? "rotate-180" : "")}
              size={20}
            />
            <div className="flex items-center gap-2 ml-2 flex-1">{getDisplayContent()}</div>
          </button>

          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
              {items.map((item) => {
                const itemId = (item as any).id;
                const isSelected = selectedIds.includes(itemId);
                return (
                  <button
                    type="button"
                    key={itemId}
                    className={clsx(
                      "w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors",
                      "first:rounded-t-lg last:rounded-b-lg border-b border-gray-200 last:border-b-0",
                      isSelected && "bg-green-50 text-green-700"
                    )}
                    onClick={() => handleSelectItem(itemId)}
                  >
                    <div className="flex items-center gap-2">
                      {renderItem(item, isSelected)}
                      {isSelected && <Check className="text-green-600" size={16} />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelectDropdown;
